import { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchMyVideos } from '../../services/videoApi';
import type { Video } from '../../services/videoApi/videoApi.types';
import {
  VideoVisibility,
  UseMyVideoListReturn,
  FetchVideosOptions,
} from './types';
import { VideoData } from '../../components/VideoCard/types';
import { useIsMobile } from '../../config/breakpoints';
import { PAGINATION } from '../../config/constants';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { SnackbarType } from '../../components/Snackbar';

/**
 * APIから返される動画データをVideoData型に変換するマッピング関数
 */
const mapVideoToVideoData = (video: Video): VideoData => {
  return {
    id: video.id,
    title: video.title,
    name: video.name,
    thumbnail: video.thumbnail || video.thumbnail_url,
    duration: video.duration,
    view_count: video.view_count,
    // hasSpecialtyAssigned（videoSpecialtyStatus.ts）が「情報自体が渡っていない」（undefined）と
    // 「診療科が本当に未設定」（specialty: null）を区別できるよう、specialty はオブジェクトごと
    // （未設定なら null を明示して）引き継ぐ。specialty_name だけの詰め替えだとこの区別ができず、
    // 診療科設定済みの動画までケバブメニューの公開系が disabled になる事故が起きる（2026-09-21）。
    specialty: video.specialty ?? null,
    specialty_name: video.specialty?.name,
    uploader: video.uploader ? {
      id: video.uploader.id,
      name: video.uploader.name,
      icon_path: video.uploader.icon_path,
      hospital: video.uploader.hospital ? {
        name: video.uploader.hospital.name
      } : undefined
    } : undefined,
    uploader_id: video.uploader_id,
    created_at: video.created_at,
    published_at: video.published_at,
    visibility: video.visibility,
    images: video.images,
    // 「資料 N」バッジ用
    materials: video.materials,
    // アップロードセッション由来の仮カード（processVideoInBackground 完了前）の判定に必要
    is_processing: video.is_processing,
    processing_status: video.processing_status,
    session_id: video.session_id,
    conversion_status: video.conversion_status,
  };
};

const PAGE_SIZE = 12;

/**
 * MyVideoListコンポーネントのビジネスロジックを管理するカスタムフック
 */
export const useMyVideoList = (): UseMyVideoListReturn => {
  const location = useLocation();
  const statusParam = new URLSearchParams(location.search).get('status') ?? 'all';
  const showSnackbar = useSnackbar();

  const [rawVideos, setRawVideos] = useState<VideoData[]>([]);
  const [specialtyOptions, setSpecialtyOptions] = useState<string[]>([]);
  const [specialty, setSpecialty] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'DESC' | 'ASC'>('DESC');
  const [isLoading, setIsLoading] = useState(false);

  const isMobile = useIsMobile();

  const filteredSortedVideos = useMemo(() => {
    const byVisibility = statusParam !== 'all'
      ? rawVideos.filter(v => v.visibility === statusParam)
      : rawVideos;
    const filtered = specialty
      ? byVisibility.filter(v => v.specialty_name === specialty)
      : byVisibility;
    return [...filtered].sort((a, b) => {
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
      return sortOrder === 'DESC' ? bDate - aDate : aDate - bDate;
    });
  }, [rawVideos, specialty, sortOrder, statusParam]);

  const videos = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredSortedVideos.slice(start, start + PAGE_SIZE);
  }, [filteredSortedVideos, currentPage]);

  const pagination = useMemo(() => ({
    currentPage,
    totalPages: Math.ceil(filteredSortedVideos.length / PAGE_SIZE) || 1,
    totalItems: filteredSortedVideos.length
  }), [filteredSortedVideos, currentPage]);

  const loadVideos = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchMyVideos({
        page: 1,
        limit: PAGINATION.FAVORITE_LIST_PAGE_SIZE,
        status: 'all',
        sortBy: 'created_at'
      } as FetchVideosOptions);

      const allVideos = (data.videos || []).map(mapVideoToVideoData);

      const specialties = Array.from(new Set(
        allVideos.map(v => v.specialty_name).filter((s): s is string => !!s)
      )).sort();

      setSpecialtyOptions(specialties);
      setRawVideos(allVideos);
      setCurrentPage(1);
    } catch {
      showSnackbar('動画の取得に失敗しました', SnackbarType.ERROR);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  // status が変わったらページをリセット
  useEffect(() => {
    setCurrentPage(1);
    setSpecialty('');
  }, [statusParam]);

  const handleSortOrderChange = useCallback((order: 'DESC' | 'ASC') => {
    setSortOrder(order);
    setCurrentPage(1);
  }, []);

  const handleSpecialtyChange = useCallback((sp: string) => {
    setSpecialty(sp);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleVisibilityChange = useCallback((_videoId: string | number, _newVisibility: VideoVisibility) => {
    loadVideos();
  }, [loadVideos]);

  return {
    isMobile,
    videos,
    pagination,
    currentPage,
    sortOrder,
    specialty,
    specialtyOptions,
    isLoading,
    handleSortOrderChange,
    handlePageChange,
    handleVisibilityChange,
    handleSpecialtyChange,
  };
};
